import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Nav, Table } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { URL } from '../../config/url'
import Dashboard from '../../services/Dashboard'
import { whatsappBirthdayLink } from '../../utilities/birthdays'
import {
  ageLabel, ageOn, lastAttendanceText, clinicDate, clinicTime, dayMonth, formatNumber, GENERAL_REFRESH_MS, joinDetail, longDateLabel, nextLabel, plural,
  progressText, unmappedStatusText,
} from '../../utilities/dashboard'
import ActionList, { OpenButton, PacienteButton, ProntuarioButton, WhatsappButton } from './ActionList'
import { BlockError, KpiSkeleton, WarningStrip } from './BlockState'
import { errorMessage, isForbidden } from './hooks'
import { CardTitle } from './InfoButton'
import useChartColors from './charts/useChartColors'
import KpiCard from './KpiCard'
import NextAgendamentos from './NextAgendamentos'

function greetingText(item) {
  if (item.greeting_status === 'SENT') return `e-mail automático: enviado${item.greeting_sent_at ? ` às ${clinicTime(item.greeting_sent_at)}` : ''}`
  if (item.greeting_status === 'FAILED') return 'e-mail automático: falha no envio'
  if (['PENDING', 'SENDING'].includes(item.greeting_status)) return 'e-mail automático: na fila'
  return ''
}

// Andamento da agenda de um doutor: realizado, falta e a atender, com um espaço de 2 px entre as partes
const AgendaProgress = ({ row }) => {
  const { chart: chartColors } = useChartColors()
  const parts = [
    { key: 'done', value: row.total_done, color: chartColors.done },
    { key: 'no-show', value: row.total_no_show, color: chartColors.noShow },
    { key: 'open', value: row.total_open, color: chartColors.open },
  ].filter((part) => part.value > 0)

  return (
    <span
      className='dashboard-stack'
      role='img'
      aria-label={`${plural(row.total_done, 'realizado', 'realizados')}, ${plural(row.total_no_show, 'falta', 'faltas')}, ${formatNumber(row.total_open)} a atender`}
    >
      {parts.map((part) => <i key={part.key} style={{ flexGrow: part.value, background: part.color }} />)}
    </span>
  )
}

const PACKAGE_TABS = [
  { key: 'ending', title: 'Terminando' },
  { key: 'exhausted', title: 'Esgotados' },
  { key: 'without_contract', title: 'PACOTE sem contrato' },
]

// Aba Geral: o dia da clínica, sem filtro. Atualiza a cada 60 s e ao voltar para a aba do navegador, só com a aba
// Geral ativa e a página visível (a mesma regra da Agenda). Na atualização automática, erro não vira alerta
const TabGeneral = ({ active, onForbidden }) => {
  const { chart: chartColors } = useChartColors()
  const navigate = useNavigate()
  const [state, setState] = useState({ loading: true, error: '', data: null, refreshError: '' })
  const [packageTab, setPackageTab] = useState('ending')
  const requestRef = useRef(0)
  const loadedAtRef = useRef(0)
  const forbiddenRef = useRef(onForbidden)
  forbiddenRef.current = onForbidden

  const load = useCallback((auto = false) => {
    const requestId = ++requestRef.current
    if (!auto) setState((current) => ({ ...current, loading: true, error: '' }))

    Dashboard.findGeneral()
      .then(({ data }) => {
        if (requestId !== requestRef.current) return
        loadedAtRef.current = Date.now()
        setState({ loading: false, error: '', data, refreshError: '' })
      })
      .catch((error) => {
        if (requestId !== requestRef.current) return
        if (isForbidden(error)) forbiddenRef.current?.()
        setState((current) => (auto && current.data)
          ? { ...current, refreshError: errorMessage(error) }
          : { ...current, loading: false, error: errorMessage(error) })
      })
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!active) return

    const refresh = () => {
      if (document.visibilityState === 'visible') load(true)
    }
    // Voltou para a aba Geral depois de mais de um minuto em outra aba do Dashboard
    if (loadedAtRef.current && (Date.now() - loadedAtRef.current > GENERAL_REFRESH_MS)) refresh()

    const interval = setInterval(refresh, GENERAL_REFRESH_MS)
    document.addEventListener('visibilitychange', refresh)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [active, load])

  const { data } = state

  if (state.error && !data) return <BlockError message={state.error} onRetry={() => load()} />

  if (!data) {
    return (
      <div className='dashboard-section'>
        <div className='dashboard-kpis'><KpiSkeleton /></div>
      </div>
    )
  }

  const agenda = data.agenda || {}
  const today = data.date
  const unmapped = unmappedStatusText(agenda.unmapped_status)
  const packages = data.packages || {}
  const packageLists = {
    ending: { total: packages.total_ending ?? 0, items: packages.ending || [] },
    exhausted: { total: packages.total_exhausted ?? 0, items: packages.exhausted || [] },
    without_contract: { total: data.package_without_contract?.total ?? 0, items: data.package_without_contract?.data || [] },
  }
  const packageList = packageLists[packageTab]

  const renderPackage = (item) => ({
    key: item.prontuario_id || item.paciente_id,
    lead: item.progresso,
    name: item.paciente_nome,
    detail: (packageTab === 'ending')
      ? joinDetail(item.titulo, item.next_start ? `próximo: ${nextLabel(item.next_start, today)}` : 'nada marcado', item.last_date ? `último: ${dayMonth(item.last_date)}` : '')
      : joinDetail(item.titulo, item.last_date ? `último: ${dayMonth(item.last_date)}` : '', item.next_start ? `próximo: ${nextLabel(item.next_start, today)}` : 'nada marcado'),
    actions: <><WhatsappButton paciente={item} /><ProntuarioButton prontuarioId={item.prontuario_id} /><PacienteButton pacienteId={item.paciente_id} /></>,
  })

  const renderWithoutContract = (item) => ({
    key: item.paciente_id,
    lead: dayMonth(clinicDate(item.last_start)),
    name: item.paciente_nome,
    detail: `${plural(item.total_sessions, 'sessão', 'sessões')} de PACOTE nos últimos 30 dias, sem contrato`,
    actions: <><ProntuarioButton prontuarioId={item.prontuario_id} latestProntuarioId={item.latest_prontuario_id} /><OpenButton pacienteId={item.paciente_id} /></>,
  })

  return (
    <div className={`dashboard-section ${state.loading ? 'dashboard-stale' : ''}`}>
      <p className='dashboard-meta'>
        {longDateLabel(today)} · atualizado às {clinicTime(data.now)} · atualiza sozinho a cada minuto
        {state.refreshError ? <span className='dashboard-meta-error'> · não foi possível atualizar agora; nova tentativa em 1 minuto</span> : ''}
      </p>

      <div className='dashboard-kpis'>
        <KpiCard label='Agendados hoje' info='general_scheduled' value={formatNumber(agenda.total)} sub={agenda.total_blocked ? `sem contar ${plural(agenda.total_blocked, 'bloqueio', 'bloqueios')} de horário` : 'nenhum bloqueio de horário'} />
        <KpiCard label='Realizados' info='general_done' value={formatNumber(agenda.total_done)} sub='REALIZADO, e AVALIAÇÃO depois do horário' />
        <KpiCard label='Faltas' info='general_no_show' value={formatNumber(agenda.total_no_show)} sub='FALTA NÃO JUSTIFICADA' />
        <KpiCard label='A atender' info='general_open' value={formatNumber(agenda.total_open)} sub={agenda.next_start ? `próximo às ${clinicTime(agenda.next_start)}` : 'nenhum horário restante hoje'} />
        <KpiCard label='Avaliações hoje' info='general_evaluations' value={formatNumber(agenda.total_evaluations)} sub='tipo de atendimento AVALIAÇÃO, sem as faltas' />
        <KpiCard
          label='Evoluções de hoje'
          info='general_records_today'
          value={`${formatNumber(agenda.total_done_visits_with_record)} de ${formatNumber(agenda.total_done_visits)}`}
          sub='visitas realizadas com atendimento no prontuário'
        />
      </div>

      {unmapped ? <WarningStrip>{unmapped} Contam como <i>A atender</i> até a lista de status ser ajustada.</WarningStrip> : ''}

      <div className='dashboard-grid'>
        <section className='dashboard-card dashboard-card-wide' aria-label='Agenda de hoje por doutor'>
          <div className='dashboard-card-head'>
            <div className='dashboard-card-heading'>
              <CardTitle title='Agenda de hoje por doutor' info='general_by_tecnico' />
              <p className='dashboard-card-sub'>bloqueios de horário (NÃO AGENDAR, REUNIÃO) ficam fora</p>
            </div>
          </div>
          {(agenda.by_tecnico || []).length ?
            <>
              <div className='dashboard-table-wrap'>
                <Table size='sm' className='dashboard-table mb-0'>
                  <thead>
                    <tr>
                      <th>Doutor</th>
                      <th className='text-end'>Agendados</th>
                      <th className='text-end'>Realizados</th>
                      <th className='text-end'>Faltas</th>
                      <th className='text-end'>A atender</th>
                      <th>Andamento</th>
                      <th className='text-end'>Próximo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agenda.by_tecnico.map((row) => (
                      <tr key={row.tecnico_id}>
                        <td>{row.nome}</td>
                        <td className='text-end dashboard-num'>{formatNumber(row.total)}</td>
                        <td className='text-end dashboard-num'>{formatNumber(row.total_done)}</td>
                        <td className='text-end dashboard-num'>{formatNumber(row.total_no_show)}</td>
                        <td className='text-end dashboard-num'>{formatNumber(row.total_open)}</td>
                        <td><AgendaProgress row={row} /></td>
                        <td className='text-end dashboard-num'>{row.next_start ? clinicTime(row.next_start) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className='dashboard-legend mt-2' aria-hidden='true'>
                <span><i className='dashboard-swatch' style={{ background: chartColors.done }} />Realizado</span>
                <span><i className='dashboard-swatch' style={{ background: chartColors.noShow }} />Falta</span>
                <span><i className='dashboard-swatch' style={{ background: chartColors.open }} />A atender</span>
              </div>
            </>
            :
            <p className='dashboard-empty'>Nenhum agendamento hoje.</p>
          }
        </section>

        {/* Calendário de cada linha: o agendamento no modal da Agenda, sem sair daqui. Salvar ou excluir recarrega a aba */}
        <NextAgendamentos items={agenda.next} onChange={() => load(true)} />

        <ActionList
          title='Faltas a reagendar'
          info='general_no_show_reschedule'
          subtitle='faltaram nos últimos 30 dias e não remarcaram nem voltaram depois'
          total={data.no_show_reschedule?.total ?? 0}
          items={data.no_show_reschedule?.data}
          emptyText='Nenhuma falta a reagendar.'
          renderItem={(item) => ({
            key: item.paciente_id,
            lead: dayMonth(item.no_show_date),
            name: item.paciente_nome,
            detail: joinDetail(item.tecnico_nome, item.tipo, progressText(item.prontuario)),
            actions: <><WhatsappButton paciente={item} /><ProntuarioButton prontuarioId={item.prontuario?.id} latestProntuarioId={item.latest_prontuario_id} /><PacienteButton pacienteId={item.paciente_id} /></>,
          })}
        />

        <ActionList
          title='Sessões sem evolução no prontuário'
          info='general_missing_records'
          subtitle='visitas realizadas nos últimos 30 dias sem atendimento lançado no mesmo dia'
          total={data.missing_records?.total ?? 0}
          items={data.missing_records?.data}
          emptyText='Todas as visitas dos últimos 30 dias têm evolução.'
          renderItem={(item) => ({
            key: item.agendamento_id,
            lead: dayMonth(item.date),
            name: item.paciente_nome,
            detail: joinDetail(item.tecnico_nome, item.status_nome, item.prontuario_id ? '' : 'sem prontuário vinculado'),
            // Agendamento sem prontuário vinculado: abre o prontuário mais recente do paciente. Sem nenhum, o botão já é "Abrir paciente"
            // e o ícone do paciente não se repete
            actions: <><OpenButton prontuarioId={item.prontuario_id} latestProntuarioId={item.latest_prontuario_id} pacienteId={item.paciente_id} />{(item.prontuario_id || item.latest_prontuario_id) ? <PacienteButton pacienteId={item.paciente_id} /> : ''}</>,
          })}
        />

        <ActionList
          title='Pacotes'
          info='general_packages'
          subtitle='pacientes em atendimento, pela regra do Progresso'
          showCount={false}
          total={packageList.total}
          items={packageList.items}
          emptyText={(packageTab === 'without_contract') ? 'Nenhum paciente com PACOTE sem contrato.' : 'Nenhum pacote nesta lista.'}
          header={
            <Nav variant='pills' className='dashboard-pills' activeKey={packageTab} onSelect={(key) => setPackageTab(key)}>
              {PACKAGE_TABS.map((tab) => (
                <Nav.Item key={tab.key}>
                  <Nav.Link eventKey={tab.key}>{tab.title} ({formatNumber(packageLists[tab.key].total)})</Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          }
          renderItem={(packageTab === 'without_contract') ? renderWithoutContract : renderPackage}
        />

        <ActionList
          title='Para reativar'
          info='general_reactivation'
          subtitle='última visita há 31 a 90 dias e nada marcado na agenda'
          total={data.reactivation?.total ?? 0}
          items={data.reactivation?.data}
          emptyText='Nenhum paciente para reativar.'
          renderItem={(item) => ({
            key: item.paciente_id,
            lead: `${formatNumber(item.days_since)} d`,
            name: item.paciente_nome,
            detail: joinDetail(item.last_visit ? `último: ${dayMonth(item.last_visit)}` : '', item.tecnico_nome, item.titulo),
            actions: <><WhatsappButton paciente={item} /><ProntuarioButton prontuarioId={item.prontuario_id} latestProntuarioId={item.latest_prontuario_id} /><PacienteButton pacienteId={item.paciente_id} /></>,
          })}
        />

        <ActionList
          title='Aniversariantes de hoje'
          info='general_birthdays'
          subtitle='pacientes ativos · mesma mensagem configurada em Aniversariantes'
          total={data.birthdays?.total ?? 0}
          items={data.birthdays?.data}
          emptyText='Nenhum aniversariante hoje.'
          renderItem={(item) => {
            const age = ageOn(item.data_nascimento, today)
            return {
              key: item.paciente_id,
              lead: ageLabel(age),
              leadWide: true,
              name: item.nome,
              detail: joinDetail(lastAttendanceText(item), greetingText(item)),
              actions: (
                <>
                  <WhatsappButton paciente={item} link={whatsappBirthdayLink(item, data.birthdays?.whatsapp_message)} label='Parabenizar' />
                  <PacienteButton pacienteId={item.paciente_id} />
                </>
              ),
            }
          }}
          footer={<div className='mt-2'><button type='button' className='dashboard-link-button' onClick={() => navigate(URL.birthdays)}>Abrir Aniversariantes</button></div>}
        />
      </div>
    </div>
  )
}

export default TabGeneral
